import { Button, InputNumber, Modal, Select, Spin } from "antd";
import {
  DeleteOutlined,
  PlusOutlined,
  MinusOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  EditOutlined,
} from "@ant-design/icons";
import React from "react";
import { useDispatch } from "react-redux";
import { svg } from "../../../../Assets/svg";
import { v4 as uuidv4 } from "uuid";
import { getVariableLabel } from "../../StockProcessManager/constants";
import { getSinglePossibleBomDimensionsAPI } from "../orderApis";

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: (localWarnings: any) => void;
  loading?: boolean;
  orderId?: string;
  warnings: {
    parts: any[];
    components: any[];
  };
  possibleBomDimensions?: {
    parts: Record<string, any>;
    components: Record<string, any>;
  };
}

const getRawValue = (val: any, unit: string) => {
  if (val === null || val === undefined) return val;
  let strVal = String(val);
  if (unit && strVal.endsWith(unit)) {
    strVal = strVal.slice(0, -unit.length);
  }
  const num = Number(strVal);
  return isNaN(num) ? strVal : num;
};

const findBackendOptions = (
  rawOptionsMap: Record<string, any>,
  varKey: string,
) => {
  if (!rawOptionsMap || !varKey) return null;
  if (
    Array.isArray(rawOptionsMap[varKey]) &&
    rawOptionsMap[varKey].length > 0
  ) {
    return rawOptionsMap[varKey];
  }
  const lower = varKey.toLowerCase();
  if (Array.isArray(rawOptionsMap[lower]) && rawOptionsMap[lower].length > 0) {
    return rawOptionsMap[lower];
  }
  const cleanTarget = String(varKey)
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  for (const [k, vals] of Object.entries(rawOptionsMap)) {
    if (
      String(k)
        .toLowerCase()
        .replace(/[\s_-]+/g, "") === cleanTarget
    ) {
      if (Array.isArray(vals) && vals.length > 0) return vals;
    }
  }
  return null;
};

const resolveValidCombo = (
  variables: any[],
  combinations: any[],
  changedIndex: number = 0,
) => {
  if (
    !Array.isArray(combinations) ||
    combinations.length === 0 ||
    !Array.isArray(variables) ||
    variables.length === 0
  ) {
    return variables;
  }

  const safeChangedIndex =
    changedIndex >= 0 && changedIndex < variables.length ? changedIndex : 0;
  const changedVar = variables[safeChangedIndex];

  // Find all candidate combinations that match the newly selected value of the changed variable
  let candidates = combinations.filter((c: any) => {
    const cVal = c[changedVar.key] ?? c[changedVar.key?.toLowerCase()];
    const rawVal = getRawValue(changedVar.currentValue, changedVar.unit);
    return String(cVal) === String(rawVal);
  });

  // If changedVar's currentValue doesn't match any combination, fallback to all combinations
  const hasMatchedCandidates = candidates.length > 0;
  if (!hasMatchedCandidates) {
    candidates = combinations;
  }

  // Check if we can find a valid candidate combination that matches the current selection as much as possible
  return variables.map((ov, index) => {
    if (index === safeChangedIndex && hasMatchedCandidates) return ov; // keep the changed one

    // Get all valid raw values for this variable from the candidate combinations
    const validRawValsForOv = candidates
      .map((c: any) => c[ov.key] ?? c[ov.key?.toLowerCase()])
      .filter((val) => val !== null && val !== undefined);

    const validFormattedValsForOv = Array.from(new Set(validRawValsForOv)).map(
      (val) => `${val}${ov.unit || ""}`,
    );

    // If the current selection of ov is within validFormattedValsForOv, we DO NOT change it!
    if (validFormattedValsForOv.includes(ov.currentValue)) {
      return ov;
    }

    // Otherwise, we prefill it with the first option from validFormattedValsForOv!
    if (validFormattedValsForOv.length > 0) {
      return { ...ov, currentValue: validFormattedValsForOv[0] };
    }

    return ov;
  });
};

const VariableConfirmationModal: React.FC<Props> = ({
  open,
  onClose,
  onConfirm,
  loading,
  orderId,
  warnings,
  possibleBomDimensions,
}) => {
  const dispatch = useDispatch();
  const [editingKeys, setEditingKeys] = React.useState<Record<string, boolean>>(
    {},
  );
  const [loadingKeys, setLoadingKeys] = React.useState<Record<string, boolean>>(
    {},
  );

  const [localWarnings, setLocalWarnings] = React.useState(() => ({
    parts: warnings.parts.map((item) => {
      const originalQty = item.qty;
      return {
        ...item,
        original_qty: originalQty,
        replacements: [
          {
            id: uuidv4(),
            qty: originalQty,
            variables: item.variables.map((v: any) => ({ ...v })),
          },
        ],
      };
    }),
    components: warnings.components.map((item) => {
      const originalQty = item.qty;
      return {
        ...item,
        original_qty: originalQty,
        replacements: [
          {
            id: uuidv4(),
            qty: originalQty,
            variables: item.variables.map((v: any) => ({ ...v })),
          },
        ],
      };
    }),
  }));

  const handleEditClick = (
    type: "part" | "component",
    itemIndex: number,
    item: any,
  ) => {
    const itemKey = `${type}_${itemIndex}_${
      type === "part" ? item.part_id : item.component_id
    }`;
    if (editingKeys[itemKey]) {
      return;
    }

    if (!orderId) {
      setEditingKeys((prev) => ({ ...prev, [itemKey]: true }));
      return;
    }

    setLoadingKeys((prev) => ({ ...prev, [itemKey]: true }));

    dispatch(
      getSinglePossibleBomDimensionsAPI(
        {
          order_id: orderId,
          type,
          part_id: item.part_id,
          component_id: item.component_id,
          bom_variant_id: item.bom_variant_id || item.bom_id,
          bom_id: item.bom_id,
        },
        (data: any) => {
          setLoadingKeys((prev) => ({ ...prev, [itemKey]: false }));
          setEditingKeys((prev) => ({ ...prev, [itemKey]: true }));

          const rawOptionsMap =
            data?.variable_options || data?.possible_values_map || {};
          const combinations = data?.variable_combinations || [];

          setLocalWarnings((prev) => {
            const arr =
              type === "part" ? [...prev.parts] : [...prev.components];
            const curItem = arr[itemIndex];
            if (!curItem) return prev;

            const updatedVariables = (curItem.variables || []).map((v: any) => {
              const existingOpts = Array.isArray(v.options) ? v.options : [];
              const backendOpts = findBackendOptions(rawOptionsMap, v.key);
              const backendFormatted =
                Array.isArray(backendOpts) && backendOpts.length > 0
                  ? backendOpts.map((val: any) => `${val}${v.unit || ""}`)
                  : [];

              const combinedOpts = Array.from(
                new Set([...existingOpts, ...backendFormatted]),
              ).sort((a: any, b: any) => parseFloat(a) - parseFloat(b));

              return {
                ...v,
                options: combinedOpts.length > 0 ? combinedOpts : existingOpts,
              };
            });

            const resolvedItemVars =
              type === "part" && combinations.length > 0
                ? resolveValidCombo(updatedVariables, combinations, 0)
                : updatedVariables;

            const updatedReplacements = curItem.replacements.map(
              (repl: any) => {
                const mappedVars = repl.variables.map((v: any) => {
                  const existingOpts = Array.isArray(v.options)
                    ? v.options
                    : [];
                  const backendOpts = findBackendOptions(rawOptionsMap, v.key);
                  const backendFormatted =
                    Array.isArray(backendOpts) && backendOpts.length > 0
                      ? backendOpts.map((val: any) => `${val}${v.unit || ""}`)
                      : [];

                  const combinedOpts = Array.from(
                    new Set([...existingOpts, ...backendFormatted]),
                  ).sort((a: any, b: any) => parseFloat(a) - parseFloat(b));

                  return {
                    ...v,
                    options:
                      combinedOpts.length > 0 ? combinedOpts : existingOpts,
                  };
                });

                const resolvedReplVars =
                  type === "part" && combinations.length > 0
                    ? resolveValidCombo(mappedVars, combinations, 0)
                    : mappedVars;

                return {
                  ...repl,
                  variables: resolvedReplVars,
                };
              },
            );

            arr[itemIndex] = {
              ...curItem,
              variables: resolvedItemVars,
              variable_combinations:
                combinations.length > 0
                  ? combinations
                  : curItem.variable_combinations,
              replacements: updatedReplacements,
            };

            return type === "part"
              ? { ...prev, parts: arr }
              : { ...prev, components: arr };
          });
        },
        () => {
          setLoadingKeys((prev) => ({ ...prev, [itemKey]: false }));
          setEditingKeys((prev) => ({ ...prev, [itemKey]: true }));
        },
      ) as any,
    );
  };

  React.useEffect(() => {
    if (!possibleBomDimensions) return;

    const partsSource =
      (possibleBomDimensions as any)?.data?.parts ||
      possibleBomDimensions.parts ||
      {};
    const compsSource =
      (possibleBomDimensions as any)?.data?.components ||
      possibleBomDimensions.components ||
      {};

    setLocalWarnings((prev) => {
      const parts = prev.parts.map((item) => {
        const partId = String(item.part_id);
        const bomId = String(item.bom_id || item.bom_variant_id || "");
        const keyWithBom = `${bomId}|${partId}`;

        let dimData =
          partsSource[partId] ||
          partsSource[keyWithBom] ||
          partsSource[`${bomId}|${partId}`];

        if (!dimData) {
          const foundKey = Object.keys(partsSource).find(
            (k) => k.endsWith(`|${partId}`) || k === partId,
          );
          if (foundKey) {
            dimData = partsSource[foundKey];
          }
        }

        const combinations =
          dimData?.variable_combinations || item.variable_combinations || [];
        const rawOptionsMap = dimData?.variable_options || {};

        const updatedVariables = (item.variables || []).map((v: any) => {
          const existingOpts = Array.isArray(v.options) ? v.options : [];
          const backendOpts = findBackendOptions(rawOptionsMap, v.key);
          const backendFormatted =
            Array.isArray(backendOpts) && backendOpts.length > 0
              ? backendOpts.map((val: any) => `${val}${v.unit || ""}`)
              : [];

          const combinedOpts = Array.from(
            new Set([...existingOpts, ...backendFormatted]),
          ).sort((a: any, b: any) => parseFloat(a) - parseFloat(b));

          const optsArray =
            combinedOpts.length > 0 ? combinedOpts : existingOpts;

          return {
            ...v,
            options: optsArray,
          };
        });

        const updatedReplacements = item.replacements.map((repl: any) => ({
          ...repl,
          variables: repl.variables.map((v: any) => {
            const existingOpts = Array.isArray(v.options) ? v.options : [];
            const backendOpts = findBackendOptions(rawOptionsMap, v.key);
            const backendFormatted =
              Array.isArray(backendOpts) && backendOpts.length > 0
                ? backendOpts.map((val: any) => `${val}${v.unit || ""}`)
                : [];

            const combinedOpts = Array.from(
              new Set([...existingOpts, ...backendFormatted]),
            ).sort((a: any, b: any) => parseFloat(a) - parseFloat(b));

            const optsArray =
              combinedOpts.length > 0 ? combinedOpts : existingOpts;

            return {
              ...v,
              options: optsArray,
            };
          }),
        }));

        return {
          ...item,
          variables: updatedVariables,
          variable_combinations: combinations,
          replacements: updatedReplacements,
        };
      });

      const components = prev.components.map((item) => {
        const compId = String(item.component_id);
        const partId = String(item.part_id || "");
        const bomId = String(item.bom_id || item.bom_variant_id || "");
        const keyWithBomPartComp = `${bomId}|${partId}|${compId}`;
        const keyWithPartComp = `${partId}|${compId}`;

        let dimData =
          compsSource[keyWithPartComp] ||
          compsSource[compId] ||
          compsSource[keyWithBomPartComp];

        if (!dimData) {
          const foundKey = Object.keys(compsSource).find(
            (k) =>
              k.endsWith(`|${partId}|${compId}`) ||
              k.endsWith(`|${compId}`) ||
              k === compId,
          );
          if (foundKey) {
            dimData = compsSource[foundKey];
          }
        }

        const combinations =
          dimData?.variable_combinations || item.variable_combinations || [];
        const rawOptionsMap = dimData?.variable_options || {};

        const updatedVariables = (item.variables || []).map((v: any) => {
          const existingOpts = Array.isArray(v.options) ? v.options : [];
          const backendOpts = findBackendOptions(rawOptionsMap, v.key);
          const backendFormatted =
            Array.isArray(backendOpts) && backendOpts.length > 0
              ? backendOpts.map((val: any) => `${val}${v.unit || ""}`)
              : [];

          const combinedOpts = Array.from(
            new Set([...existingOpts, ...backendFormatted]),
          ).sort((a: any, b: any) => parseFloat(a) - parseFloat(b));

          const optsArray =
            combinedOpts.length > 0 ? combinedOpts : existingOpts;

          return {
            ...v,
            options: optsArray,
          };
        });

        const updatedReplacements = item.replacements.map((repl: any) => ({
          ...repl,
          variables: repl.variables.map((v: any) => {
            const existingOpts = Array.isArray(v.options) ? v.options : [];
            const backendOpts = findBackendOptions(rawOptionsMap, v.key);
            const backendFormatted =
              Array.isArray(backendOpts) && backendOpts.length > 0
                ? backendOpts.map((val: any) => `${val}${v.unit || ""}`)
                : [];

            const combinedOpts = Array.from(
              new Set([...existingOpts, ...backendFormatted]),
            ).sort((a: any, b: any) => parseFloat(a) - parseFloat(b));

            const optsArray =
              combinedOpts.length > 0 ? combinedOpts : existingOpts;

            return {
              ...v,
              options: optsArray,
            };
          }),
        }));

        return {
          ...item,
          variables: updatedVariables,
          variable_combinations: combinations,
          replacements: updatedReplacements,
        };
      });

      return { parts, components };
    });
  }, [possibleBomDimensions]);

  const handleUpdateRowQty = (
    type: "part" | "component",
    itemIndex: number,
    rowId: string,
    newQty: number,
  ) => {
    setLocalWarnings((prev) => {
      const arr = type === "part" ? [...prev.parts] : [...prev.components];
      const item = arr[itemIndex];
      if (item) {
        item.replacements = item.replacements.map((row: any) => {
          if (row.id === rowId) {
            return { ...row, qty: newQty };
          }
          return row;
        });
        arr[itemIndex] = { ...item };
      }
      return type === "part"
        ? { ...prev, parts: arr }
        : { ...prev, components: arr };
    });
  };

  const handleSplitRow = (
    type: "part" | "component",
    itemIndex: number,
    rowId: string,
  ) => {
    setLocalWarnings((prev) => {
      const arr = type === "part" ? [...prev.parts] : [...prev.components];
      const item = arr[itemIndex];
      if (item) {
        const rowIndex = item.replacements.findIndex(
          (r: any) => r.id === rowId,
        );
        if (rowIndex !== -1) {
          const row = item.replacements[rowIndex];
          if (row.qty > 1) {
            const splitQty1 = Math.floor(row.qty / 2);
            const splitQty2 = row.qty - splitQty1;
            const updatedRow = { ...row, qty: splitQty1 };
            const newRow = {
              ...row,
              id: uuidv4(),
              qty: splitQty2,
              variables: row.variables.map((v: any) => ({ ...v })),
            };
            const newReplacements = [...item.replacements];
            newReplacements[rowIndex] = updatedRow;
            newReplacements.splice(rowIndex + 1, 0, newRow);
            item.replacements = newReplacements;
            arr[itemIndex] = { ...item };
          }
        }
      }
      return type === "part"
        ? { ...prev, parts: arr }
        : { ...prev, components: arr };
    });
  };

  const handleRemoveRow = (
    type: "part" | "component",
    itemIndex: number,
    rowId: string,
  ) => {
    setLocalWarnings((prev) => {
      const arr = type === "part" ? [...prev.parts] : [...prev.components];
      const item = arr[itemIndex];
      if (item && item.replacements.length > 1) {
        item.replacements = item.replacements.filter(
          (r: any) => r.id !== rowId,
        );
        arr[itemIndex] = { ...item };
      }
      return type === "part"
        ? { ...prev, parts: arr }
        : { ...prev, components: arr };
    });
  };

  const variablesConfig = [
    { key: "height", label: "Height", isNumeric: true },
    { key: "reinforced_height", label: "Reinforced Height", isNumeric: true },
    { key: "width", label: "Width", isNumeric: true },
    { key: "depth", label: "Depth", isNumeric: true },
    { key: "length", label: "Length", isNumeric: true },
    { key: "thickness", label: "Thickness", isNumeric: true },
    { key: "gauge", label: "Gauge", isNumeric: true },
    { key: "level_width", label: "Level Width", isNumeric: true },
    { key: "level_depth", label: "Level Depth", isNumeric: true },
    {
      key: "bolts_per_footplate",
      label: "Bolts per Footplate",
      isNumeric: true,
      isHideMM: true,
    },
    {
      key: "dividers_per_shelf",
      label: "Dividers per Shelf",
      isNumeric: true,
      isHideMM: true,
    },
    {
      key: "connecting_points",
      label: "Connecting Points",
      isNumeric: true,
      isHideMM: true,
    },
    {
      key: "holes_drilled",
      label: "Holes Drilled",
      isNumeric: true,
      isHideMM: true,
    },
    { key: "first_beam_level", label: "1st Beam Level", isNumeric: true },
    { key: "frame_type", label: "Frame Type", isNumeric: false },
    { key: "foot_type", label: "Foot Type", isNumeric: false },
    {
      key: "shelf_reinforcers",
      label: "Shelf Reinforcers",
      isNumeric: true,
      isHideMM: true,
    },
    { key: "crossties", label: "Crossties", isNumeric: true, isHideMM: true },
    {
      key: "pallet_supports",
      label: "Pallet Supports",
      isNumeric: true,
      isHideMM: true,
    },
    {
      key: "chipboard_supports",
      label: "Chipboard Supports",
      isNumeric: true,
      isHideMM: true,
    },
    { key: "v_type", label: "Type", isNumeric: false },
    { key: "drop", label: "Drop", isNumeric: false },
    { key: "custom_drop", label: "Custom Drop", isNumeric: true },
    { key: "first_shelf_level", label: "1st Shelf Level", isNumeric: true },
    { key: "finish", label: "Finish", isNumeric: false },
    { key: "color", label: "Color", isNumeric: false },
  ];

  const handleAddRow = (type: "part" | "component", itemIndex: number) => {
    setLocalWarnings((prev) => {
      const arr = type === "part" ? [...prev.parts] : [...prev.components];
      const item = arr[itemIndex];
      if (item) {
        const allocatedQty = item.replacements.reduce(
          (sum: number, r: any) => sum + (r.qty || 0),
          0,
        );
        const remainingQty = item.original_qty - allocatedQty;
        const newRow = {
          id: uuidv4(),
          qty: remainingQty > 0 ? remainingQty : 1,
          variables: item.variables.map((v: any) => ({ ...v })),
        };
        item.replacements = [...item.replacements, newRow];
        arr[itemIndex] = { ...item };
      }
      return type === "part"
        ? { ...prev, parts: arr }
        : { ...prev, components: arr };
    });
  };

  const handleReplVariableChange = (
    type: "part" | "component",
    itemIndex: number,
    rowId: string,
    varIndex: number,
    value: string,
  ) => {
    setLocalWarnings((prev) => {
      const arr = type === "part" ? [...prev.parts] : [...prev.components];
      const item = arr[itemIndex];
      if (item) {
        item.replacements = item.replacements.map((row: any) => {
          if (row.id === rowId) {
            let vars = [...row.variables];
            vars[varIndex] = { ...vars[varIndex], currentValue: value };
            if (
              type === "part" &&
              Array.isArray(item.variable_combinations) &&
              item.variable_combinations.length > 0
            ) {
              vars = resolveValidCombo(
                vars,
                item.variable_combinations,
                varIndex,
              );
            }
            return { ...row, variables: vars };
          }
          return row;
        });
        arr[itemIndex] = { ...item };
      }
      return type === "part"
        ? { ...prev, parts: arr }
        : { ...prev, components: arr };
    });
  };

  const groupedParts = React.useMemo(() => {
    const groups: Record<
      string,
      {
        variantKey: string;
        variantName: string;
        variantNames?: string[];
        variantDimensions?: Record<string, number>;
        items: { item: any; originalIndex: number }[];
      }
    > = {};

    localWarnings.parts.forEach((item: any, index: number) => {
      const vKey = item.bom_variant_id || item.variantName || "default";
      if (!groups[vKey]) {
        groups[vKey] = {
          variantKey: vKey,
          variantName: item.variantName || "Product Variant",
          variantNames: item.variantNames,
          variantDimensions: item.variant_dimensions || {},
          items: [],
        };
      }
      groups[vKey].items.push({ item, originalIndex: index });
    });

    return Object.values(groups);
  }, [localWarnings.parts]);

  const getGroupValidations = React.useCallback(
    (group: {
      variantKey: string;
      variantName: string;
      variantNames?: string[];
      variantDimensions?: Record<string, number>;
      items: { item: any; originalIndex: number }[];
    }) => {
      const dims = group.variantDimensions || {};
      const relationsByProdVar: Record<
        string,
        { item: any; part_variable: string }[]
      > = {};

      group.items.forEach(({ item }) => {
        const relation = item.variable_relation;
        if (relation && relation.part_variable && relation.product_variable) {
          const pVar = relation.product_variable.toLowerCase();
          if (!relationsByProdVar[pVar]) {
            relationsByProdVar[pVar] = [];
          }
          relationsByProdVar[pVar].push({
            item,
            part_variable: relation.part_variable.toLowerCase(),
          });
        }
      });

      const validations: {
        productVariable: string;
        productVariableLabel: string;
        targetValue: number;
        currentSum: number;
        diff: number;
        isMatched: boolean;
        participatingPartsCount: number;
      }[] = [];

      Object.entries(relationsByProdVar).forEach(([pVar, partMappings]) => {
        const targetVal = dims[pVar];
        if (targetVal === undefined || targetVal === null || targetVal <= 0) {
          return;
        }

        let totalSum = 0;
        partMappings.forEach(({ item, part_variable }) => {
          let partAllocatedQty = 0;
          let partTotalVal = 0;
          let fallbackSingleVal: number | null = null;

          (item.replacements || []).forEach((repl: any) => {
            const vObj = (repl.variables || []).find(
              (v: any) =>
                (v.key || "").toLowerCase() === part_variable.toLowerCase(),
            );
            if (
              vObj &&
              vObj.currentValue !== undefined &&
              vObj.currentValue !== null
            ) {
              const raw = getRawValue(vObj.currentValue, vObj.unit);
              const num = Number(raw);
              const replQty = Number(repl.qty || 0);
              if (!isNaN(num)) {
                fallbackSingleVal = num;
                partTotalVal += num * replQty;
                partAllocatedQty += replQty;
              }
            }
          });

          if (partAllocatedQty > 0) {
            totalSum += partTotalVal / partAllocatedQty;
          } else if (fallbackSingleVal !== null) {
            totalSum += fallbackSingleVal;
          }
        });

        const roundedSum = Math.round(totalSum * 100) / 100;
        const diff = Math.round((roundedSum - targetVal) * 100) / 100;
        const isMatched = Math.abs(diff) < 0.01;

        validations.push({
          productVariable: pVar,
          productVariableLabel: getVariableLabel(pVar),
          targetValue: targetVal,
          currentSum: roundedSum,
          diff,
          isMatched,
          participatingPartsCount: partMappings.length,
        });
      });

      return validations;
    },
    [],
  );

  const isContinueDisabled = React.useMemo(() => {
    const allGroups = [...localWarnings.parts, ...localWarnings.components];
    const hasQtyMismatch = allGroups.some((item) => {
      const allocatedQty = item.replacements.reduce(
        (sum: number, r: any) => sum + (r.qty || 0),
        0,
      );
      return item.original_qty !== allocatedQty;
    });
    if (hasQtyMismatch) return true;

    for (const group of groupedParts) {
      const validations = getGroupValidations(group);
      if (validations.some((v) => !v.isMatched)) {
        return true;
      }
    }

    return false;
  }, [localWarnings, groupedParts, getGroupValidations]);

  const hasAnyDimensionMismatch = React.useMemo(() => {
    return groupedParts.some((group) => {
      const validations = getGroupValidations(group);
      return validations.some((v) => !v.isMatched);
    });
  }, [groupedParts, getGroupValidations]);

  const renderPartItemCard = (
    item: any,
    itemIndex: number,
    type: "part" | "component",
  ) => {
    const itemKey = `${type}_${itemIndex}_${
      type === "part" ? item.part_id : item.component_id
    }`;
    const isEditing = Boolean(editingKeys[itemKey]);
    const isLoading = Boolean(loadingKeys[itemKey]);

    const allocatedQty = item.replacements.reduce(
      (sum: number, r: any) => sum + (r.qty || 0),
      0,
    );
    const remainingQty = item.original_qty - allocatedQty;

    return (
      <div
        key={itemIndex}
        className="card bg-light rounded border mb-3 shadow-sm"
        style={{ overflow: "hidden" }}
      >
        {/* Item Card Header */}
        <div className="card-header bg-white border-bottom p-3 d-flex justify-content-between align-items-start gap-2">
          <div style={{ flex: 1, minWidth: 0 }}>
            {type === "component" && (
              <div
                className="mb-1"
                style={{
                  fontSize: "11px",
                  color: "#888",
                  display: "flex",
                  flexDirection: "column",
                  gap: "2px",
                }}
              >
                {Array.isArray(item.variantNames) &&
                item.variantNames.length > 0 ? (
                  <ol
                    className="m-0 pl-3"
                    style={{
                      listStyleType: "decimal",
                      paddingLeft: "15px",
                    }}
                  >
                    {item.variantNames.map((n: string, i: number) => (
                      <li key={i}>{n}</li>
                    ))}
                  </ol>
                ) : (
                  <div>{item.variantName}</div>
                )}
                {Array.isArray(item.parentPartNames) &&
                item.parentPartNames.length > 1 ? (
                  <div>
                    Parts:{" "}
                    {item.parentPartNames
                      .map((n: string, i: number) => `${i + 1}. ${n}`)
                      .join(", ")}
                  </div>
                ) : item.parentPartName ? (
                  <div>Part: {item.parentPartName}</div>
                ) : null}
              </div>
            )}
            <div className="fw-bold fs-6" style={{ color: "#222" }}>
              - {item.itemName}
            </div>
            <small className="text-muted">
              Total items to configure:{" "}
              <span className="fw-bold text-dark">{item.original_qty}</span>
            </small>
          </div>

          <div className="d-flex align-items-center gap-2">
            <div
              className={`badge ${
                remainingQty === 0 ? "bg-success" : "bg-warning text-dark"
              } p-2 px-3 rounded-pill flex-shrink-0`}
              style={{
                fontSize: "12px",
                minWidth: "100px",
                textAlign: "center",
              }}
            >
              Remaining: {remainingQty}
            </div>

            {!isEditing &&
              (isLoading ? (
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{ width: "32px", height: "32px" }}
                >
                  <Spin size="small" />
                </div>
              ) : (
                <button
                  type="button"
                  className="btn btn-link p-0 svg-32 d-inline-flex align-items-center justify-content-center border-0"
                  onClick={() => handleEditClick(type, itemIndex, item)}
                  title="Edit"
                  style={{ cursor: "pointer", background: "none" }}
                >
                  {svg?.editBtn}
                </button>
              ))}
          </div>
        </div>

        {/* Item Card Body */}
        {isLoading ? (
          <div className="card-body p-4 d-flex align-items-center justify-content-center">
            <Spin tip="Fetching dimensions..." />
          </div>
        ) : !isEditing ? (
          <div className="card-body p-3 d-flex flex-column gap-2">
            {item.replacements.map((repl: any, rIdx: number) => (
              <div
                key={repl.id || rIdx}
                className="p-2 px-3 bg-white rounded border d-flex flex-wrap align-items-center justify-content-between gap-2"
              >
                <div className="d-flex flex-wrap align-items-center gap-2">
                  {Array.isArray(repl.variables) &&
                    repl.variables.map((v: any, vIndex: number) => {
                      const VarName =
                        variablesConfig?.find(
                          (elem: any) => elem?.key === v?.key,
                        )?.label ||
                        v?.key ||
                        "";
                      return (
                        <div
                          key={vIndex}
                          className="px-2 py-1 bg-light rounded border d-inline-flex align-items-center gap-1"
                          style={{ fontSize: "12px" }}
                        >
                          <span
                            className="text-muted fw-semibold"
                            style={{ textTransform: "capitalize" }}
                          >
                            {VarName}:
                          </span>
                          <span className="fw-bold text-dark">
                            {v.currentValue || "-"}
                          </span>
                        </div>
                      );
                    })}
                </div>

                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted small fw-semibold">Qty:</span>
                  <span
                    className="badge bg-secondary px-2 py-1"
                    style={{ fontSize: "12px" }}
                  >
                    {repl.qty}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="card-body p-3 d-flex flex-column gap-3">
            {item.replacements.map((repl: any) => (
              <div
                key={repl.id}
                className="p-3 bg-white rounded border position-relative"
                style={{ borderStyle: "solid" }}
              >
                <div className="row gy-3 align-items-end page-form pe-5">
                  {/* Variable Dropdowns */}
                  {Array.isArray(repl.variables) &&
                    repl.variables.map((v: any, vIndex: number) => {
                      let displayOptions = v.options || [];
                      if (
                        v.currentValue !== null &&
                        v.currentValue !== undefined &&
                        v.currentValue !== "" &&
                        !displayOptions.includes(v.currentValue)
                      ) {
                        displayOptions = [v.currentValue, ...displayOptions];
                      }

                      const VarName =
                        variablesConfig?.find(
                          (elem: any) => elem?.key === v?.key,
                        )?.label || "";
                      return (
                        <div key={vIndex} className="col-auto">
                          <label
                            className="small fw-bold mb-1 d-block"
                            style={{ textTransform: "capitalize" }}
                          >
                            {VarName || v.key}
                          </label>
                          <Select
                            className="auto-height-select"
                            popupClassName="full-text-select-dropdown"
                            suffixIcon={svg?.selectDownArrow}
                            value={v.currentValue}
                            onChange={(val) =>
                              handleReplVariableChange(
                                type,
                                itemIndex,
                                repl.id,
                                vIndex,
                                val,
                              )
                            }
                            style={{ minWidth: "120px" }}
                            options={displayOptions.map((opt: string) => ({
                              label: opt,
                              value: opt,
                            }))}
                            showSearch
                          />
                        </div>
                      );
                    })}

                  {/* Qty Stepper */}
                  <div className="col-auto">
                    <label className="small fw-bold mb-1 d-block">Qty</label>
                    <div
                      className="form-control form-control-lg d-flex align-items-center justify-content-between p-1 itemAddRemoveInput"
                      style={{ width: "150px" }}
                    >
                      <Button
                        type="text"
                        icon={<MinusOutlined />}
                        className="btn btn-primary addRemoveBtn"
                        onClick={() =>
                          handleUpdateRowQty(
                            type,
                            itemIndex,
                            repl.id,
                            Math.max(1, repl.qty - 1),
                          )
                        }
                        disabled={repl.qty <= 1}
                      />
                      <InputNumber
                        value={repl.qty}
                        min={1}
                        max={item.original_qty}
                        onWheel={(e) => e.currentTarget.blur()}
                        onChange={(val) =>
                          handleUpdateRowQty(type, itemIndex, repl.id, val || 1)
                        }
                        className="border-0 text-center w-100 shadow-none"
                        controls={false}
                      />
                      <Button
                        type="text"
                        icon={<PlusOutlined />}
                        className="btn btn-primary addRemoveBtn"
                        onClick={() =>
                          handleUpdateRowQty(
                            type,
                            itemIndex,
                            repl.id,
                            Math.min(item.original_qty, repl.qty + 1),
                          )
                        }
                        disabled={
                          repl.qty >= item.original_qty || remainingQty <= 0
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Actions Corner (Split & Delete) */}
                <div
                  className="position-absolute top-0 end-0 mt-2 me-2 d-flex gap-2 align-items-center"
                  style={{ width: "auto" }}
                >
                  {repl.qty > 1 && (
                    <Button
                      type="text"
                      size="small"
                      className="text-primary fw-bold p-0"
                      onClick={() => handleSplitRow(type, itemIndex, repl.id)}
                      style={{ fontSize: "12px" }}
                    >
                      Split
                    </Button>
                  )}
                  {item.replacements.length > 1 && (
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveRow(type, itemIndex, repl.id)}
                      className="p-0 d-flex align-items-center justify-content-center"
                      style={{ width: "24px", height: "24px" }}
                    />
                  )}
                </div>
              </div>
            ))}

            {/* Add Another Configuration Button at the bottom of card */}
            {remainingQty > 0 && (
              <div className="mt-2 text-start">
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => handleAddRow(type, itemIndex)}
                  size="small"
                >
                  Add Another Configuration
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderGroupedParts = () => {
    if (!groupedParts || groupedParts.length === 0) return null;
    return (
      <div className="mb-4">
        <h6
          className="fw-bold mb-3"
          style={{
            fontSize: "15px",
            color: "#333",
            borderBottom: "1px solid #eee",
            paddingBottom: "6px",
          }}
        >
          Parts
        </h6>
        {groupedParts.map((group, gIndex) => {
          const validations = getGroupValidations(group);
          const hasMismatches = validations.some((v) => !v.isMatched);

          return (
            <div
              key={gIndex}
              className="border rounded mb-4 shadow-sm"
              style={{
                backgroundColor: "#fcfcfc",
                borderColor: hasMismatches ? "#ff4d4f" : "#e0e0e0",
                overflow: "hidden",
              }}
            >
              {/* Product Variant Group Header */}
              <div
                className="p-3 border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2"
                style={{
                  backgroundColor: "#ffffff",
                }}
              >
                <div>
                  <div
                    className="fw-bold text-dark fs-6"
                    style={{ display: "flex", flexDirection: "column" }}
                  >
                    {Array.isArray(group.variantNames) &&
                    group.variantNames.length > 0 ? (
                      <ol
                        className="m-0 pl-3"
                        style={{
                          listStyleType: "decimal",
                          paddingLeft: "15px",
                        }}
                      >
                        {group.variantNames.map((n: string, i: number) => (
                          <li key={i}>{n}</li>
                        ))}
                      </ol>
                    ) : (
                      <span>{group.variantName}</span>
                    )}
                  </div>
                  {validations.length > 0 && (
                    <div className="d-flex flex-wrap gap-3 mt-1">
                      {validations.map((val, vIdx) => (
                        <small key={vIdx} className="text-muted">
                          Original Product {val.productVariableLabel}:{" "}
                          <span className="fw-bold text-dark">
                            {val.targetValue}mm
                          </span>
                        </small>
                      ))}
                    </div>
                  )}
                </div>

                {validations.length > 0 && (
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    {validations.map((val, vIdx) =>
                      val.isMatched ? (
                        <span
                          key={vIdx}
                          className="badge bg-success p-2 px-3 rounded-pill text-white fw-medium d-inline-flex align-items-center gap-1"
                          style={{ fontSize: "12px" }}
                        >
                          <CheckCircleOutlined />
                          Total {val.productVariableLabel}: {val.currentSum}mm
                          (Matched)
                        </span>
                      ) : (
                        <span
                          key={vIdx}
                          className="badge bg-danger p-2 px-3 rounded-pill text-white fw-medium d-inline-flex align-items-center gap-1"
                          style={{ fontSize: "12px" }}
                        >
                          <WarningOutlined />
                          {val.productVariableLabel} Difference:{" "}
                          {val.diff > 0 ? `+${val.diff}` : val.diff}mm (Current:{" "}
                          {val.currentSum}mm / Target: {val.targetValue}mm)
                        </span>
                      ),
                    )}
                  </div>
                )}
              </div>

              {/* Group Body: Parts List */}
              <div className="p-3 d-flex flex-column gap-2">
                {group.items.map(({ item, originalIndex }) =>
                  renderPartItemCard(item, originalIndex, "part"),
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderComponents = () => {
    if (!localWarnings.components || localWarnings.components.length === 0)
      return null;
    return (
      <div className="mb-4">
        <h6
          className="fw-bold mb-3"
          style={{
            fontSize: "15px",
            color: "#333",
            borderBottom: "1px solid #eee",
            paddingBottom: "6px",
          }}
        >
          Components
        </h6>
        {localWarnings.components.map((item, index) =>
          renderPartItemCard(item, index, "component"),
        )}
      </div>
    );
  };

  return (
    <Modal
      centered
      open={open}
      onCancel={onClose}
      footer={null}
      closeIcon={svg?.closeIcon}
      width={"80rem"}
      className="commonModalClass"
      destroyOnClose={true}
      title={
        <div className="text-center fs-4 fw-bolder lh-m mb-4">
          Confirm Component/Part Variables
        </div>
      }
    >
      <div className="d-flex flex-column gap-3 py-2 px-1">
        <div className="px-2" style={{ maxHeight: "60vh", overflowY: "auto" }}>
          {renderComponents()}
          {renderGroupedParts()}
        </div>

        {hasAnyDimensionMismatch && (
          <div className="alert alert-danger py-2 px-3 mb-0 text-center fs-13 d-flex align-items-center justify-content-center gap-2">
            <WarningOutlined />
            <span>
              The total dimensions of the parts must equal the original
              product variant total before continuing.
            </span>
          </div>
        )}

        <p className="text-center mb-0 fs-6 mt-1" style={{ color: "#666" }}>
          Please review and confirm all variable dimensions before proceeding.
        </p>

        <div className="d-flex justify-content-center gap-3 mt-3">
          <Button
            onClick={onClose}
            className="btn btn-lg btn-outline-secondary h-auto px-5"
          >
            CLOSE
          </Button>

          <Button
            onClick={() => onConfirm(localWarnings)}
            loading={loading}
            disabled={isContinueDisabled}
            className="btn btn-lg btn-primary h-auto px-5"
          >
            SAVE & CONTINUE
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default VariableConfirmationModal;
